import type { RoadRestoreResponse, RoadRestoreShort } from '@goodtime/shared';
import { api, ApiError } from './client';
import { withRetry } from './road';

/**
 * Restore landmark `landmark` on this slot's road. Every retry of one tap
 * sends the same key, so a retry after a lost answer gets the server's
 * first answer back and pays once (and asking again later for a landmark
 * already restored charges nothing). Throws an {@link ApiError}: 402 when
 * the player is short ({@link restoreShort}), 409 when an earlier landmark
 * comes first.
 */
export function restoreLandmark(
  sessionId: string,
  themeId: string,
  landmark: number,
): Promise<RoadRestoreResponse> {
  const key = `restore-${themeId}-${landmark}-${Math.random().toString(36).slice(2, 10)}`;
  return withRetry(() =>
    api<RoadRestoreResponse>(`/sessions/${encodeURIComponent(sessionId)}/road/restore`, {
      method: 'POST',
      headers: { 'Idempotency-Key': key },
      body: JSON.stringify({ themeId, landmark }),
    }),
  );
}

/** The 402 body when the player can't afford a landmark, else null. */
export function restoreShort(err: unknown): RoadRestoreShort | null {
  if (!(err instanceof ApiError) || err.status !== 402) return null;
  try {
    const body = JSON.parse(err.body) as Partial<RoadRestoreShort>;
    return body.error === 'NOT_ENOUGH_COINS' ? (body as RoadRestoreShort) : null;
  } catch {
    return null;
  }
}
